/* eslint-disable react/prop-types */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import buildRequestUrl from "lib/requests/requestUtils";
import athenaClient from "lib/axios/clients";
import { useEffectAsync } from "lib/react/customHooks";
import { makeStyles, Typography } from "@material-ui/core";
import _ from "lodash";
import { GoLocation } from "react-icons/go";
import { AiOutlineShop, AiOutlineTags } from "react-icons/ai";
import CardComponent from "app/common/CardComponent";
import moment from "moment";
import ElasticityTimeSeriesChartComponent from "./ElasticityTimeSeriesChartComponent";
import ExplicabilityComponent from "../../explicability-info/ExplicabilityComponent";
import SelectComponent from "../SelectComponent";
import { setTupleData } from "../duck/slices";

const useStyles = makeStyles((theme) => ({
  aggregateContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "5px",
  },
  chartContainer: {
    position: "relative",
    overflow: "hidden",
    flexGrow: "1",
    [theme.breakpoints.up("xs")]: {
      height: "155px",
    },
    [theme.breakpoints.up("sm")]: {
      height: "200px",
    },
    [theme.breakpoints.up("md")]: {
      height: "300px",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
  explicabilityContainer: {
    marginLeft: "-8rem",
    marginTop: "-0.12rem",
  },
}));

function ElasticityTimeSeriesComponent({ filtersKey, queryFilters }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const filtersSlice = useSelector((state) => state.filters);
  const elasticitiesSlice = useSelector((state) => state.elasticities);
  const catalogSlice = useSelector((state) => state.catalog);
  const retailersSlice = useSelector((state) => state.retailers);
  const retailersGroupSlice = useSelector((state) => state.retailersGroup);
  const areasSlice = useSelector((state) => state.areas);

  const selectOptions = [
    {
      key: null,
      label: "Aggregated",
    },
    {
      key: "category_id",
      label: "By Category",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "line_id",
      label: "By Line",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "brand_id",
      label: "By Brand",
      icon: <AiOutlineTags size={22} />,
    },
    {
      key: "retailer_group_id",
      label: "By Retailer Group",
      icon: <AiOutlineShop size={22} />,
    },
    {
      key: "retailer_id",
      label: "By Retailer",
      icon: <AiOutlineShop size={22} />,
    },
    {
      key: "area_id",
      label: "By Area",
      icon: <GoLocation size={22} />,
    },
  ];

  const [elasticityData, setElasticityData] = React.useState(null);
  const [timeSeriesGroupBy, setTimeSeriesGroupBy] = React.useState({
    key: null,
    label: "Aggregated",
  });

  const retrieveElasticityForTuple = async (tupleKey) => {
    if (
      elasticitiesSlice.tuples[tupleKey] &&
      elasticitiesSlice.tuples[tupleKey].timeSeries
    ) {
      return {
        key: tupleKey,
        timeSeries: elasticitiesSlice.tuples[tupleKey].timeSeries,
        labeledDates: elasticitiesSlice.tuples[tupleKey].labeledDates,
      };
    }

    const requestQueryFilters = {
      ...queryFilters,
      is_product_client: 1,
      data_source_id: 1,
    };

    const elasticitiesUrl = buildRequestUrl(
      {
        filters: requestQueryFilters,
        group_by: timeSeriesGroupBy.key
          ? [timeSeriesGroupBy.key, "date"]
          : ["date"],
        avg_weight_by_pred_units_last_price: ["elasticity"],
        order_by: ["date"],
        order_direction: ["desc"],
        join_by: "target",
        filter_type: "same",
        min_date: moment()
          .startOf("month")
          .format("YYYY-MM-DD"),
        max_date: moment()
          .add(1, "years")
          .endOf("month")
          .format("YYYY-MM-DD"),
      },
      "elasticities"
    );

    const elasticitiesConfig = {
      method: "GET",
      url: elasticitiesUrl,
    };

    const elasticityResult = await athenaClient(elasticitiesConfig);

    let elasticities = _.orderBy(elasticityResult.data, "date", "asc");

    const uniqueElasticitiesByDate = _.pickBy(
      _.uniqBy(elasticities, "date"),
      _.identity
    );

    const labeledDates = _.map(
      _.sortBy(uniqueElasticitiesByDate, "date", "asc"),
      (elasticity) => {
        return moment(elasticity.date).format("MMM'YY");
      }
    );

    if (timeSeriesGroupBy.key) {
      elasticities = _.groupBy(elasticities, timeSeriesGroupBy.key);
    }

    return {
      key: tupleKey,
      timeSeries: elasticities,
      labeledDates,
    };
  };

  const getLabelForGroupBy = (itemId) => {
    if (itemId === "null") return ""; // There are null values coming from the database and the lodash groupBy places them into the "null" property
    switch (timeSeriesGroupBy.key) {
      case "retailer_id":
        return `${retailersSlice[itemId].label}`;
      case "area_id":
        return `${areasSlice[itemId].label}`;
      case "brand_id":
        return `${catalogSlice.brands[itemId].label}`;
      case "line_id":
        return `${catalogSlice.lines[itemId].label}`;
      case "category_id":
        return `${catalogSlice.categories[itemId].label}`;
      case "retailer_group_id":
        return `${retailersGroupSlice[itemId].label}`;
      default:
        return "Aggregated";
    }
  };

  const getDatasetFromElasticityTimeSeries = (
    elasticityTimeSeries,
    timeSeriesCatalogItemId,
    alpha
  ) => {
    return {
      label: `Elasticity-${timeSeriesCatalogItemId}`,
      yAxisID: "elasticity",
      type: "line",
      fill: false,
      borderColor: `rgba(165, 21, 87,${alpha})`,
      pointBordercolor: `rgba(165, 21, 87,${alpha})`,
      pointBackgroundColor: "#ffffff",
      backgroundColor: `rgba(165, 21, 87,${alpha})`,
      hoverBorderWidth: 3,
      hoverBorderColor: "#A51557",
      pointHoverBackgroundColor: "#ffffff",
      pointHoverRadius: 6,
      hoverBorderDash: [4, 3],
      data: _.map(elasticityTimeSeries, (elasticity) => {
        return {
          ...elasticity,
          x: moment(elasticity.date).format("MMM'YY"),
          y: elasticity.avg_weight_by_pred_units_last_price_elasticity,
          label: `${getLabelForGroupBy(timeSeriesCatalogItemId)}`,
          avg_weight_by_pred_units_last_price_elasticity: {
            absolute: elasticity.avg_weight_by_pred_units_last_price_elasticity,
          },
        };
      }),
    };
  };

  useEffectAsync(
    async () => {
      const tupleKey = `timeseries-${filtersKey}-${timeSeriesGroupBy.key}`;

      return retrieveElasticityForTuple(tupleKey);
    },

    (result) => {
      dispatch(
        setTupleData({
          [result.key]: {
            timeSeries: result.timeSeries,
            labeledDates: result.labeledDates,
          },
        })
      );

      const totalDatasetsCount = _.size(
        _.pickBy(result.timeSeries, (value, key) => key !== "null")
      );

      let index = 0;

      setElasticityData({
        labels: result.labeledDates,
        datasets: timeSeriesGroupBy.key
          ? _.map(
              result.timeSeries,
              (elasticityTimeSeries, timeSeriesCatalogItemId) => {
                index += 1;
                return getDatasetFromElasticityTimeSeries(
                  elasticityTimeSeries,
                  timeSeriesCatalogItemId,
                  index / totalDatasetsCount // Alpha value for the opacity of this dataset in the chart
                );
              }
            )
          : [getDatasetFromElasticityTimeSeries(result.timeSeries, null, 1)],
      });
    },
    [filtersSlice.filters, timeSeriesGroupBy.key]
  );

  return (
    <>
      {elasticityData && (
        <CardComponent
          title={
            <>
              <Typography variant="body1" className={classes.componentTitle}>
                Predictive Own Elasticity
                <ExplicabilityComponent
                  tooltipText={
                    <>
                      See how sensible your products will be to their own price
                      changes month by month to find the best moment for doing a
                      promotion. See this information aggregated at any level
                      using the drop down list. Own elasticity can be described
                      as <strong>elastic</strong> ({"<"} - 1) —very responsive—
                      or <strong>inelastic</strong> ({">"} - 1) not very
                      responsive.{" "}
                    </>
                  }
                />
              </Typography>
              <div className={classes.aggregateContainer}>
                <SelectComponent
                  selectValue={timeSeriesGroupBy}
                  setSelectValue={setTimeSeriesGroupBy}
                  selectTitle="Aggregation"
                  selectOptions={selectOptions}
                />
              </div>
            </>
          }
          body={
            <div className={classes.chartContainer}>
              <ElasticityTimeSeriesChartComponent
                groupBy={timeSeriesGroupBy}
                setGroupBy={setTimeSeriesGroupBy}
                data={elasticityData}
              />
            </div>
          }
        />
      )}
    </>
  );
}

export default ElasticityTimeSeriesComponent;
